import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { BinaryRendererProps } from '../types'
import { RendererLoading, RendererError } from './RendererStatus'
import { buildWorkbook, sheetLocale, type GridCell, type GridSheet } from './sheetModel'
import { logError } from '@/lib/logger'

/**
 * Spreadsheet preview: the workbook as a sheet grid, read from the file itself
 * (cdb_webos#1435). Values follow each cell's number format in the reader's
 * language; the computed results come from the file (TQS caches them when it
 * writes a workbook, cdb_tqs#2131). Not a PDF on purpose — A4 pages cut off
 * columns (#536).
 */
export function ExcelRenderer({ fileUrl }: BinaryRendererProps) {
  const { t, i18n } = useTranslation()
  const locale = sheetLocale(i18n.language)
  const [sheets, setSheets] = useState<GridSheet[]>([])
  const [activeSheet, setActiveSheet] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!fileUrl) return
    let cancelled = false

    const loadExcel = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetch(fileUrl)
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const arrayBuffer = await response.arrayBuffer()
        // Dynamic import keeps SheetJS out of the main bundle.
        const XLSX = await import('@e965/xlsx')
        const workbook = XLSX.read(arrayBuffer, { type: 'array', cellStyles: true, cellDates: true, cellNF: true })
        const parsed = buildWorkbook(XLSX, workbook, locale)
        if (!cancelled) {
          setSheets(parsed)
          setActiveSheet(0)
        }
      } catch (err) {
        logError(err as Error, { context: 'ExcelRenderer', fileUrl })
        if (!cancelled) setError(t('filePreview.errorLoading'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadExcel()
    return () => { cancelled = true }
  }, [fileUrl, locale, t])

  const current = sheets[activeSheet]
  const tableWidth = useMemo(
    () => (current ? ROW_HEADER_PX + current.columns.reduce((sum, c) => sum + c.width, 0) : 0),
    [current],
  )

  if (loading) return <RendererLoading />
  if (error) return <RendererError message={error} />
  if (!current) return null

  return (
    <div
      className="flex-1 min-h-0 flex flex-col rounded-lg border overflow-hidden"
      style={{ backgroundColor: 'var(--sidebar-background)', borderColor: 'var(--sidebar-border)' }}
      data-testid="excel-preview"
    >
      <div className="flex-1 min-h-0 overflow-scroll" style={{ scrollbarGutter: 'stable' }}>
        {current.rows.length === 0 ? (
          <p className="p-4 text-sm text-[var(--text-secondary)]">{t('filePreview.sheetEmpty')}</p>
        ) : (
          <table
            className="file-preview-sheet text-sm"
            style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', width: tableWidth }}
          >
            <colgroup>
              <col style={{ width: ROW_HEADER_PX }} />
              {current.columns.map((column) => (
                <col key={column.letter} style={{ width: column.width }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th className="sticky top-0 left-0 z-20" style={HEADER_STYLE} aria-hidden="true" />
                {current.columns.map((column) => (
                  <th key={column.letter} scope="col" style={HEADER_STYLE} className="sticky top-0 z-10 font-normal">
                    {column.letter}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {current.rows.map((row) => (
                <tr key={row.number}>
                  <th
                    scope="row"
                    className="sticky left-0 z-[5] font-normal text-right pr-2"
                    style={HEADER_STYLE}
                  >
                    {row.number}
                  </th>
                  {row.cells.map((cell, index) =>
                    cell ? <SheetCell key={index} cell={cell} /> : null,
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {(current.hiddenRows > 0 || current.hiddenCols > 0) && (
          <p className="px-3 py-2 text-xs text-[var(--text-secondary)]" data-testid="excel-preview-truncated">
            {t('filePreview.sheetTruncated', {
              parts: [
                current.hiddenRows > 0 && t('filePreview.sheetTruncatedRows', { count: current.hiddenRows }),
                current.hiddenCols > 0 && t('filePreview.sheetTruncatedCols', { count: current.hiddenCols }),
              ].filter(Boolean).join(', '),
            })}
          </p>
        )}
      </div>

      {sheets.length > 1 && (
        <div
          className="flex-shrink-0 flex gap-0.5 px-2 pb-2 overflow-x-auto border-t"
          style={{ borderColor: 'var(--window-border-color)' }}
          role="tablist"
        >
          {sheets.map((sheet, idx) => (
            <button
              key={sheet.name}
              role="tab"
              aria-selected={idx === activeSheet}
              onClick={() => setActiveSheet(idx)}
              className="px-3 py-1.5 text-xs font-medium rounded-b-md transition-colors whitespace-nowrap"
              style={
                idx === activeSheet
                  ? { backgroundColor: 'var(--theme-interactive-color)', color: 'var(--theme-interactive-foreground)' }
                  : { backgroundColor: 'transparent', color: 'var(--text-secondary)' }
              }
            >
              {sheet.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const ROW_HEADER_PX = 44

const HEADER_STYLE = {
  backgroundColor: 'var(--window-background)',
  color: 'var(--text-secondary)',
  borderRight: '1px solid var(--window-border-color)',
  borderBottom: '1px solid var(--window-border-color)',
  fontSize: '0.75rem',
  padding: '2px 4px',
} as const

function SheetCell({ cell }: { cell: GridCell }) {
  const numeric = cell.kind === 'number' || cell.kind === 'date'
  return (
    <td
      colSpan={cell.colSpan}
      rowSpan={cell.rowSpan}
      title={cell.formula ? `${cell.text}  ${cell.formula}` : cell.text || undefined}
      data-kind={cell.kind}
      className="px-2 py-1 whitespace-nowrap overflow-hidden text-ellipsis"
      style={{
        textAlign: numeric ? 'right' : cell.kind === 'bool' || cell.kind === 'error' ? 'center' : 'left',
        fontVariantNumeric: numeric ? 'tabular-nums' : undefined,
        color: cell.kind === 'error' ? 'var(--cdx-color-danger)' : cell.fill?.color ?? 'var(--text-color)',
        backgroundColor: cell.fill?.background,
        borderRight: '1px solid var(--window-border-color)',
        borderBottom: '1px solid var(--window-border-color)',
      }}
    >
      {cell.text}
    </td>
  )
}
